<template>
    <div class="container">
        <br />
        <br />
        <div v-show="!noData" class="row">
            <div class="col-12 mb-4 mb-md-5">
                <div class="d-flex flex-column flex-lg-row mb-3">
                    <div class="flex-grow-1">
                        <h1 class="h4 font-weight-bolder mb-2">
                            <span class="mr-2">History</span>
                        </h1>
                        <p class="text-secondary mb-2">
                            Resume a scenario session where you left off, or download a proof of completion.
                        </p>
                    </div>
                </div>
                <div>
                    <ul class="nav">
                        <li class="nav-item tab-nav mr-1">
                            <a
                                class="nav-link tab-nav-link"
                                :class="{ active: activeTab == 'progress' }"
                                @click="setActiveTab('progress')"
                            >
                                <span class="mr-2">In Progress</span>
                                <span class="badge badge-gray text-primary align-text-bottom">{{
                                    sessions.length
                                }}</span>
                            </a>
                        </li>
                        <li class="nav-item tab-nav mr-1">
                            <a
                                class="nav-link tab-nav-link"
                                :class="{ active: activeTab == 'complete' }"
                                @click="setActiveTab('complete')"
                            >
                                <span class="mr-2">Complete</span>
                                <span class="badge badge-gray text-success align-text-bottom">{{
                                    successfulSessions.length
                                }}</span>
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
            <div class="col-12" v-if="activeTab == 'progress'">
                <div v-for="(session, index) in sessions" :key="session.id">
                    <div class="d-flex flex-column flex-md-row align-items-md-center">
                        <div class="flex-grow-0 flex-shrink-0 mr-md-4 mr-lg-5 mb-4 mb-md-0">
                            <router-link
                                :to="{ name: 'CommunityCaseDetail', params: { id: session.case.id } }"
                                class="no-decor"
                            >
                                <community-case-cover
                                    class="cover-container"
                                    :case-obj="session.case"
                                ></community-case-cover>
                            </router-link>
                            <community-case-progress-bar
                                :session="session"
                                class="progress-container mt-3"
                            ></community-case-progress-bar>
                        </div>
                        <div class="flex-grow-1">
                            <div class="mb-4 mb-md-2">
                                <h2 class="h6 mb-2 case-title">
                                    <span class="align-middle">{{ session.case.community_title }}</span>
                                </h2>
                                <p class="text-secondary community-text-body mb-3">
                                    {{ session.case.community_description }}
                                </p>
                                <div class="mb-4 mb-lg-3">
                                    <button @click="openRestart(session)" class="btn btn-gray mr-2" type="button">
                                        <span class="icon icon-glyphicons-basic-restart icon-mr"></span>
                                        <span>Restart</span>
                                    </button>
                                    <router-link
                                        v-if="session.is_active"
                                        :to="sessionRouteObj(session)"
                                        class="btn btn-brand mr-2"
                                        :disabled="loading"
                                        type="button"
                                    >
                                        <span class="icon icon-glyphicons-basic-play icon-mr"></span>
                                        <span>Resume</span>
                                    </router-link>
                                    <button
                                        class="btn btn-gray"
                                        aria-label="Delete in progress session"
                                        @click="openRemoveSession(session)"
                                    >
                                        <span class="icon icon-glyphicons-basic-bin"></span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <hr class="my-4 pb-1" v-show="index < sessions.length - 1" />
                </div>
            </div>
            <div class="col-12" v-else>
                <div v-for="(session, index) in successfulSessions" :key="session.id">
                    <div class="d-flex flex-column flex-md-row align-items-md-center">
                        <div class="flex-grow-0 flex-shrink-0 mr-md-4 mr-lg-5 mb-4 mb-md-0">
                            <router-link
                                :to="{ name: 'CommunityCaseDetail', params: { id: session.case.id } }"
                                class="no-decor"
                            >
                                <community-case-cover
                                    class="cover-container"
                                    :case-obj="session.case"
                                ></community-case-cover>
                            </router-link>
                        </div>
                        <div class="flex-grow-1">
                            <div class="mb-4 mb-md-2">
                                <h2 class="h6 mb-2 case-title">
                                    <span class="align-middle">{{ session.case.community_title }}</span>
                                    <span
                                        v-show="session.successful"
                                        class="badge badge-success align-middle complete-text ml-2"
                                    >
                                        Successful
                                    </span>
                                </h2>
                                <p class="text-secondary community-text-body mb-2">
                                    {{ session.case.community_description }}
                                </p>
                                <p class="mb-3">
                                    <span v-show="session.completion_time" class="font-weight-500 small align-middle">
                                        Completed {{ displayDate(session.completion_time) }}
                                    </span>
                                </p>
                                <div class="mb-0">
                                    <a
                                        :href="`/export/sessions/${session.id}/response-summary`"
                                        target="_blank"
                                        rel="noopener"
                                        class="btn btn-gray"
                                    >
                                        <span class="icon icon-glyphicons-basic-cloud-download icon-mr"></span>
                                        <span>Completion PDF</span></a
                                    >
                                </div>
                            </div>
                        </div>
                    </div>
                    <hr class="my-4 pb-1" v-show="index < successfulSessions.length - 1" />
                </div>
                <div v-show="successfulSessions.length === 0">
                    <p class=" text-secondary">
                        Scenarios you've successfully completed will show up here.
                    </p>
                </div>
            </div>
        </div>
        <br />
        <br />
        <!-- Empty Display -->
        <div class="col-12" v-if="noData">
            <br />
            <br />
            <br />
            <br />
            <div class="d-flex justify-content-center mb-5">
                <img src="/static/general/img/undraw_box.svg" height="200" alt="" />
            </div>
            <div class="d-flex justify-content-center mb-3">
                <p class="text-center text-secondary mb-0">You haven't attempted any scenarios yet.</p>
            </div>
            <div class="d-flex justify-content-center mb-4">
                <router-link class="btn btn-primary" :to="{ name: 'CommunityLibrary' }">
                    Browse the Library
                </router-link>
            </div>
        </div>
        <!-- Restart Modal -->
        <base-modal id="restart-modal" :centered="true">
            <template slot="title">
                <span class="icon icon-glyphicons-basic-restart mr-1"></span>
                <span>Restart Scenario</span>
            </template>
            <template>
                <p v-if="selectedSession" class="my-2">
                    Restart <strong>{{ selectedSession.case.community_title }}</strong
                    >?
                </p>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="restart()" class="btn btn-brand" type="button">
                    <span class="icon icon-glyphicons-basic-restart icon-mr"></span>
                    <span>Restart</span>
                </button>
            </template>
        </base-modal>
        <!-- Remove Modal -->
        <base-modal id="remove-modal" :centered="true" :loading="modalLoading">
            <template slot="title">
                <span class="icon icon-glyphicons-basic-bin mr-1"></span>
                <span>Remove In Progress Session</span>
            </template>
            <template>
                <p v-if="selectedSession" class="my-2">
                    Remove session progress for scenario <strong>{{ selectedSession.case.community_title }}</strong
                    >?
                </p>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="remove()" :disabled="modalLoading" class="btn btn-danger" type="button">
                    <span>Remove</span>
                </button>
            </template>
        </base-modal>
    </div>
</template>

<script>
import http from "@/axios.js"
import Utilities from "@/assets/js/utilities.js"
import CommunityCaseCover from "@/components/CommunityCaseCover"
import CommunityCaseProgressBar from "@/components/CommunityCaseProgressBar"
import BaseModal from "@/components/BaseModal"

export default {
    name: "CommunityCaseSessions",
    components: {
        CommunityCaseCover,
        CommunityCaseProgressBar,
        BaseModal,
    },
    data() {
        return {
            sessions: [],
            successfulSessions: [],
            selectedSession: null,
            activeTab: "progress",
            modalLoading: false,
        }
    },
    computed: {
        loading() {
            return this.$store.getters.loading
        },
        noData() {
            return this.sessions.length === 0 && !this.loading
        },
    },
    methods: {
        fetchData() {
            let vm = this
            this.$store.dispatch("addLoad")
            http.get("/api/community/sessions/")
                .then(function (response) {
                    vm.sessions = response.data
                })
                .finally(function () {
                    vm.$store.dispatch("removeLoad")
                })
            this.$store.dispatch("addLoad")
            http.get("/api/community/sessions/successful/")
                .then(function (response) {
                    vm.successfulSessions = response.data
                })
                .finally(function () {
                    vm.$store.dispatch("removeLoad")
                })
        },
        sessionRouteObj(session) {
            return {
                name: "CommunityCaseSession",
                params: { caseID: String(session.case.id), sessionID: String(session.id) },
            }
        },
        openRestart(session) {
            this.selectedSession = session
            $("#restart-modal").modal("show")
        },
        openRemoveSession(session) {
            this.selectedSession = session
            $("#remove-modal").modal("show")
        },
        restart() {
            let vm = this
            http.post(`/api/community/cases/${this.selectedSession.case.id}/start/`)
                .then(function (response) {
                    vm.$store.dispatch("removeLoad")
                    $("#restart-modal").modal("hide")
                    vm.$router.push({
                        name: "CommunityCaseSession",
                        params: {
                            caseID: String(vm.caseID),
                            sessionID: String(response.data.session_id),
                        },
                    })
                })
                .finally(function () {
                    vm.$store.dispatch("removeLoad")
                })
        },
        remove() {
            let vm = this
            this.modalLoading = true
            http.put(`/api/community/sessions/${this.selectedSession.id}/remove/`)
                .then(() => {
                    $("#remove-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Session removed.",
                        data: {},
                    })
                    vm.fetchData()
                })
                .finally(() => {
                    vm.modalLoading = false
                })
        },
        setActiveTab(tabName) {
            this.activeTab = tabName
        },
        displayDate(dateStr) {
            return Utilities.formatDateTimeCondensed(dateStr)
        },
    },
    mounted() {
        this.fetchData()
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/community.scss";

.cover-container {
    height: 200px;
    width: 100%;
    @media (min-width: $breakpoint-sm) {
        height: 170px;
        width: 280px;
    }
    max-width: 100%;
}

.progress-container {
    @media (min-width: $breakpoint-sm) {
        max-width: 280px;
    }
}

.complete-text {
    font-weight: 600;
    font-size: 10px;
    color: $success;
    border: 1px solid $success !important;
    background-color: #ffffff;
}
</style>